import { cn } from "@/lib/cn";
import { useInstall } from "@/components/providers/install-provider";
import { Dialog } from "@/components/ui/dialog";
import { Download, X } from "lucide-react";

/**
 * One-time branded banner for the browser's install prompt. Once dismissed it
 * never returns — the persistent entry point lives in the sidebar and topbar.
 */
export function InstallPrompt() {
  const { showBanner, install, dismissBanner } = useInstall();

  if (!showBanner) return null;

  return (
    <div className="no-print fixed inset-x-3 bottom-24 z-40 mx-auto max-w-md rounded-2xl border border-border bg-surface p-4 shadow-xl sm:left-auto sm:right-4 lg:bottom-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--brand-primary)] text-white">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-text">Install STEP-TEMBER</p>
          <p className="mt-0.5 text-sm text-text-muted">
            Log activity in one tap, straight from your home screen.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => void install()}
              className="inline-flex h-9 items-center rounded-xl bg-[var(--brand-primary)] px-4 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismissBanner}
              className="inline-flex h-9 items-center rounded-xl px-4 text-sm font-semibold text-text-muted transition hover:bg-surface-muted"
            >
              Not now
            </button>
          </div>
          <p className="mt-2 text-xs text-text-subtle">
            You can install later from the menu any time.
          </p>
        </div>
        <button
          type="button"
          onClick={dismissBanner}
          aria-label="Dismiss install prompt"
          className="text-text-subtle transition hover:text-text"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Shown when the browser gives us no `beforeinstallprompt` to replay — Edge
 * frequently withholds it, and Safari/Firefox never send one at all. Rather
 * than hide the action, point at where each browser keeps it.
 */
export function InstallHelpDialog() {
  const { showHelp, closeHelp } = useInstall();

  return (
    <Dialog
      open={showHelp}
      onClose={closeHelp}
      title="Install STEP-TEMBER"
      description="Your browser handles the install itself. Here is where to find it."
    >
      <ul className="space-y-3 text-sm text-text">
        <li>
          <span className="font-semibold">Edge</span> — open the{" "}
          <span className="font-semibold">···</span> menu, then{" "}
          <span className="font-semibold">Apps → Install this site as an app</span>.
          There is also an install icon in the address bar.
        </li>
        <li>
          <span className="font-semibold">Chrome</span> — open the{" "}
          <span className="font-semibold">⋮</span> menu, then{" "}
          <span className="font-semibold">Cast, save and share → Install page as app</span>.
        </li>
        <li>
          <span className="font-semibold">Safari (iPhone or iPad)</span> — tap{" "}
          <span className="font-semibold">Share</span>, then{" "}
          <span className="font-semibold">Add to Home Screen</span>.
        </li>
        <li>
          <span className="font-semibold">Safari (Mac)</span> — choose{" "}
          <span className="font-semibold">File → Add to Dock</span>.
        </li>
      </ul>
      <p className="mt-4 text-sm text-text-muted">
        Already installed it? Browsers hide the install action once the app is on
        your machine — look for STEP-TEMBER in your app list instead.
      </p>
    </Dialog>
  );
}

/** Sidebar row that keeps the install action reachable after a dismissal. */
export function InstallNavRow({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { canInstall, install } = useInstall();

  if (!canInstall) return null;

  return (
    <button
      type="button"
      onClick={() => {
        void install();
        onNavigate?.();
      }}
      title={collapsed ? "Install app" : undefined}
      className={cn(
        "group relative mb-2 flex items-center rounded-xl border border-dashed border-[var(--brand-accent)]/50 text-sm font-medium text-white/75 transition-colors hover:bg-white/8 hover:text-white",
        collapsed ? "h-11 w-11 justify-center" : "h-11 w-full gap-3 px-3",
      )}
    >
      <Download className="h-[18px] w-[18px] shrink-0 text-[var(--brand-accent)]" />
      {!collapsed && <span className="truncate">Install app</span>}
      {collapsed && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-full z-50 ml-3 hidden whitespace-nowrap rounded-lg bg-[var(--ink)] px-2.5 py-1.5 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 lg:block"
        >
          Install app
        </span>
      )}
    </button>
  );
}

/** Compact topbar action, so mobile users see it without opening the drawer. */
export function InstallTopbarButton() {
  const { canInstall, install } = useInstall();

  if (!canInstall) return null;

  return (
    <button
      type="button"
      onClick={() => void install()}
      aria-label="Install app"
      title="Install app"
      className="flex h-10 w-10 items-center justify-center rounded-xl text-[var(--brand-primary)] transition-colors hover:bg-surface-muted"
    >
      <Download className="h-5 w-5" />
    </button>
  );
}
